import { useState } from 'react';
import { Loader2, MapPin, X } from 'lucide-react';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import { useCreateVisit, useUpdateVisit } from '@/hooks/useVisits';
import { ApiConflictError, ApiValidationError } from '@/lib/api';
import { toDateInput } from '@/lib/activityTime';
import {
  emptyVisitForm,
  isToday,
  toVisitInput,
  validateVisitForm,
  visitFormFrom,
  visitServerErrors,
  type StaffVisit,
  type VisitForm,
  type VisitFormErrors,
} from '@/lib/visitForm';
import { FormField } from '@/components/activities/ActivityUi';

interface VisitModalProps {
  participantId: number;
  participantName: string;
  /** The visit to correct; omit to record a new one. */
  visit?: StaffVisit;
  /** Where a new visit starts: the participant's usual centre. */
  defaultCenterId: number | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

/** Records a visit nobody checked in, or corrects one: centre, day and times. */
export default function VisitModal({
  participantId,
  participantName,
  visit,
  defaultCenterId,
  onClose,
  onSuccess,
}: VisitModalProps) {
  const editing = !!visit;
  const create = useCreateVisit(participantId);
  const update = useUpdateVisit(participantId);
  const centers = useDigitalCenters({ limit: 100 });
  const pending = create.isPending || update.isPending;

  const [form, setForm] = useState<VisitForm>(() =>
    visit ? visitFormFrom(visit) : emptyVisitForm(defaultCenterId),
  );
  const [errors, setErrors] = useState<VisitFormErrors>({});
  const [formError, setFormError] = useState('');

  const set = <K extends keyof VisitForm>(key: K, value: VisitForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clientErrors = validateVisitForm(form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }
    const data = toVisitInput(form);
    try {
      if (visit) {
        await update.mutateAsync({ id: visit.id, data });
        onSuccess('Visita corrigida.');
      } else {
        await create.mutateAsync(data);
        onSuccess('Visita registada.');
      }
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setErrors(visitServerErrors(err.errors));
      } else if (err instanceof ApiConflictError) {
        setFormError(`${participantName} já tem outra visita registada a essa hora.`);
      } else {
        setFormError('Não foi possível guardar a visita.');
      }
    }
  };

  const invalid = (key: keyof VisitForm) =>
    errors[key] ? 'border-destructive focus:ring-destructive/30' : '';
  const today = toDateInput(new Date());
  const centerOptions = centers.data?.digitalCenters ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="visit-modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            <h2 id="visit-modal-title" className="text-lg font-semibold text-foreground">
              {editing ? 'Corrigir visita' : 'Registar visita'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form id="visit-form" onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-4 overflow-y-auto">
          <p className="text-sm text-muted-foreground">{participantName}</p>

          <FormField label="Centro Digital" htmlFor="visit-center" required error={errors.digitalCenterId}>
            <select
              id="visit-center"
              className={`input ${invalid('digitalCenterId')}`}
              value={form.digitalCenterId ?? ''}
              onChange={(e) => set('digitalCenterId', e.target.value ? Number(e.target.value) : null)}
              disabled={centers.isLoading}
            >
              <option value="">{centers.isLoading ? 'A carregar…' : 'Escolhe o centro'}</option>
              {centerOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.centerName}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Dia" htmlFor="visit-date" required error={errors.date}>
            <input
              id="visit-date"
              type="date"
              className={`input ${invalid('date')}`}
              value={form.date}
              max={today}
              onChange={(e) => set('date', e.target.value)}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Entrada" htmlFor="visit-check-in" required error={errors.checkIn}>
              <input
                id="visit-check-in"
                type="time"
                className={`input ${invalid('checkIn')}`}
                value={form.checkIn}
                onChange={(e) => set('checkIn', e.target.value)}
              />
            </FormField>
            <FormField
              label="Saída"
              htmlFor="visit-check-out"
              required={!isToday(form.date)}
              error={errors.checkOut}
              hint={isToday(form.date) ? 'Vazia se ainda estiver no centro.' : undefined}
            >
              <input
                id="visit-check-out"
                type="time"
                className={`input ${invalid('checkOut')}`}
                value={form.checkOut}
                onChange={(e) => set('checkOut', e.target.value)}
              />
            </FormField>
          </div>

          {formError && (
            <p className="text-sm text-destructive" role="alert">
              {formError}
            </p>
          )}
        </form>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="visit-form"
            disabled={pending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {pending && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? 'Guardar' : 'Registar'}
          </button>
        </div>
      </div>
    </div>
  );
}
